import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Button, Column, DataTable, Field, Input, LinkButton, PageHeader, Select } from '../../components/ui';
import { useRemote } from '../../features/home/useRemote';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import type { RootState } from '../../store';
import type { User } from '../../types/User';
import { fullName } from '../Users/fullName';
import { AREA_IDS, AREAS, AuditEntry, describe, isArea, LOCATION_AREAS, placeOf } from './audit';
import { AuditQuery, useAuditLog } from './useAuditLog';

dayjs.extend(relativeTime);

const DAY = 'YYYY-MM-DD';
const ALL = 'all';
/** Every change except sign-ins, which would bury them; the Area filter has a Sign-ins option. */
const CHANGE_ACTIONS = AREA_IDS.filter((a) => a !== 'signin').flatMap((a) => AREAS[a]).join(',');

const formatTime = (iso: string): string => dayjs(iso).format(dayjs(iso).isSame(dayjs(), 'year') ? 'D MMM, HH:mm' : 'D MMM YYYY, HH:mm');

/** "Who changed what" for the active venue. Filters live in the URL so a view survives reload and can be shared. */
const ActivityLog = () => {
    const { t, i18n } = useTranslation('activity');
    usePageTitle(t('title'));
    const [params, setParams] = useSearchParams();
    const { restaurantId, locationId, isAdmin } = useActiveLocation();
    const role = useSelector((s: RootState) => s.auth.user?.role?.name);
    const canManage = role === 'Admin' || role === 'Owner';

    const today = dayjs().format(DAY);
    const defaultFrom = dayjs().subtract(29, 'day').format(DAY);
    const from = params.get('from') ?? defaultFrom;
    const to = params.get('to') ?? today;
    const areaParam = params.get('area');
    const area = isArea(areaParam) ? areaParam : null;
    const actorId = params.get('actor');
    const filtered = params.has('from') || params.has('to') || area !== null || actorId !== null;

    const query = useMemo<AuditQuery | null>(
        () =>
            isAdmin && !restaurantId
                ? null
                : {
                      from: dayjs(from).startOf('day').toISOString(),
                      to: dayjs(to).endOf('day').toISOString(),
                      action: area ? AREAS[area].join(',') : CHANGE_ACTIONS,
                      actorId: actorId ?? undefined,
                      // Menu, team and venue changes belong to the whole venue, not to one location.
                      locationId: area !== null && LOCATION_AREAS.includes(area) && locationId ? locationId : undefined,
                      restaurantId: isAdmin ? (restaurantId ?? undefined) : undefined,
                  },
        [isAdmin, restaurantId, from, to, area, actorId, locationId]
    );
    const log = useAuditLog(query);

    // Only an Owner or Admin may list the team; a Manager picks from people seen in the log.
    const team = useRemote(canManage ? 'team' : null, () => api.get<User[]>('/users').then((r) => r.data));
    const members = useMemo(() => {
        const byId = new Map<string, string>();
        team.data
            ?.filter((u) => !restaurantId || !u.restaurant || u.restaurant.id === restaurantId)
            .forEach((u) => byId.set(u.id, fullName(u)));
        if (!canManage) log.rows.forEach((r) => r.actorId && byId.set(r.actorId, r.actorName));
        if (actorId && !byId.has(actorId)) byId.set(actorId, log.rows.find((r) => r.actorId === actorId)?.actorName ?? actorId);
        return [...byId].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, i18n.language));
    }, [team.data, restaurantId, canManage, log.rows, actorId, i18n.language]);

    const setFilter = (key: string, value: string | null) =>
        setParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (value && value !== ALL) next.set(key, value);
                else next.delete(key);
                return next;
            },
            { replace: true }
        );

    const columns: Column<AuditEntry>[] = [
        {
            id: 'time',
            header: t('time'),
            priority: 'meta',
            width: '10rem',
            cell: (e) => (
                <time dateTime={e.createdAt} title={dayjs(e.createdAt).fromNow()} className="whitespace-nowrap">
                    {formatTime(e.createdAt)}
                </time>
            ),
        },
        {
            id: 'who',
            header: t('who'),
            priority: 'secondary',
            width: '12rem',
            cell: (e) => {
                const guestOrSystem = e.actorId === null && i18n.exists(`values.${e.actorRole}`, { ns: 'activity' });
                const roleName = i18n.exists(`values.${e.actorRole}`, { ns: 'activity' }) ? t(`values.${e.actorRole}` as 'values.none') : e.actorRole;
                return (
                    <span className="block">
                        <span className="block font-medium text-strong">{guestOrSystem ? roleName : e.actorName}</span>
                        {!guestOrSystem && <span className="block text-caption text-muted">{roleName}</span>}
                    </span>
                );
            },
        },
        {
            id: 'what',
            header: t('what'),
            priority: 'primary',
            cell: (e) => <span className="text-small font-medium text-strong">{describe(e, t, i18n)}</span>,
        },
        {
            id: 'place',
            header: t('place'),
            priority: 'meta',
            width: '11rem',
            cell: (e) => {
                const place = placeOf(e, canManage);
                return place ? (
                    <LinkButton to={place.to} variant="link" className="text-small">
                        {t(`link.${place.kind}`)}
                    </LinkButton>
                ) : (
                    <span className="text-muted" aria-hidden="true">
                        –
                    </span>
                );
            },
        },
    ];

    return (
        <div>
            <PageHeader title={t('title')} meta={t('meta')} />
            <form
                role="search"
                aria-label={t('filters.label')}
                onSubmit={(e) => e.preventDefault()}
                className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
            >
                <Field label={t('filters.from')}>
                    {(c) => <Input {...c} type="date" value={from} max={to} onChange={(e) => e.target.value && setFilter('from', e.target.value)} />}
                </Field>
                <Field label={t('filters.to')}>
                    {(c) => <Input {...c} type="date" value={to} min={from} onChange={(e) => e.target.value && setFilter('to', e.target.value)} />}
                </Field>
                <Field label={t('filters.area')}>
                    {(c) => (
                        <Select
                            id={c.id}
                            aria-describedby={c['aria-describedby']}
                            value={area ?? ALL}
                            onChange={(v) => setFilter('area', v)}
                            options={[{ value: ALL, label: t('filters.allAreas') }, ...AREA_IDS.map((a) => ({ value: a as string, label: t(`areas.${a}`) }))]}
                        />
                    )}
                </Field>
                <Field label={t('filters.member')}>
                    {(c) => (
                        <Select
                            id={c.id}
                            aria-describedby={c['aria-describedby']}
                            value={actorId ?? ALL}
                            onChange={(v) => setFilter('actor', v)}
                            options={[{ value: ALL, label: t('filters.everyone') }, ...members]}
                        />
                    )}
                </Field>
                {filtered && (
                    <div className="sm:col-span-2 lg:col-span-4">
                        <Button variant="quiet" size="sm" onClick={() => setParams({}, { replace: true })}>
                            {t('filters.reset')}
                        </Button>
                    </div>
                )}
            </form>

            <DataTable
                rows={log.rows}
                columns={columns}
                getRowId={(e) => e.id}
                state={log.state}
                onRetry={log.reload}
                errorTitle={t('loadFailed')}
                caption={t('caption')}
                pageSize={Number.MAX_SAFE_INTEGER}
                tableFrom="lg"
                empty={{ icon: ClipboardDocumentListIcon, title: t('empty.title'), body: t('empty.body') }}
            />

            {log.hasMore && (
                <div className="mt-4 flex flex-col items-center gap-2">
                    {log.more === 'error' && (
                        <p role="alert" className="text-small text-danger">
                            {t('loadMoreFailed')}
                        </p>
                    )}
                    <Button variant="outline" loading={log.more === 'loading'} onClick={log.loadMore}>
                        {log.more === 'loading' ? t('loadingMore') : t('loadMore')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ActivityLog;
